import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { simulationV2 } from '~/services/simulation-service';
import type { SimulationShipmentCountParams } from '~/services/simulation-service/simulation';
import { useQueryCreator } from '~/hooks/useQueryCreator';
import { useDebounce } from '~/hooks/useDebounce';
import type { SimulationServiceVersion } from '~/store/simulation/types';

const DEBOUNCE_MS = 400;

function errorMessageFrom(error: unknown): string {
  const data = axios.isAxiosError(error) ? error.response?.data : undefined;
  if (data && typeof data === 'object') {
    const { message, error: err } = data as { message?: string; error?: string };
    if (message) return message;
    if (err) return err;
  }
  return 'Unable to calculate shipment count. Please try again.';
}

/**
 * Exact in-window shipment count for the selected v2 uploads (pre-sampling).
 * v2-only: disabled for v1 (its batcher already honors the range server-side) and when no uploads
 * are selected. Dates are both-or-neither — a half-filled range is dropped to a whole-file count so
 * the server never 400s on a partial window. Debounced so dragging the date picker doesn't spam.
 */
export function useSimulationShipmentCount(input: {
  version: SimulationServiceVersion;
  uploadIds: string[];
  startDate?: string;
  endDate?: string;
}): { count: number | undefined; isLoading: boolean; errorMessage: string | undefined } {
  const { version, uploadIds, startDate, endDate } = input;

  const params = useMemo<SimulationShipmentCountParams>(() => {
    const unique = [...new Set(uploadIds)];
    return startDate && endDate ? { uploadIds: unique, startDate, endDate } : { uploadIds: unique };
  }, [uploadIds, startDate, endDate]);

  const [debounced, setDebounced] = useState<SimulationShipmentCountParams>(params);
  const debounce = useDebounce(setDebounced as (p: SimulationShipmentCountParams) => void, DEBOUNCE_MS);
  useEffect(() => debounce(params), [debounce, params]);

  const query = useQuery({
    ...useQueryCreator(simulationV2.shipmentCount, { args: [debounced] }),
    enabled: version === 'v2' && debounced.uploadIds.length > 0,
    retry: false, // a 400 (bad range / unvalidated upload) is deterministic — don't hammer it
  });

  return {
    count: query.data,
    isLoading: query.isFetching,
    errorMessage: query.isError ? errorMessageFrom(query.error) : undefined,
  };
}
